import type { Engagement, ExperienceRole } from '@portfolio/shared';
import type { JSX } from 'react';
import { Link } from 'react-router';

import { ChipList } from '@/components/ui/Chip';
import { ArrowRightIcon } from '@/components/ui/icons';

interface ExperienceTimelineProps {
  roles: readonly ExperienceRole[];
  /** Compact mode hides engagement detail, used on the home page. */
  variant?: 'full' | 'compact';
}

/**
 * An ordered list, so screen readers announce position and count. The visual
 * timeline rail is decorative CSS on top of that structure.
 */
export function ExperienceTimeline({
  roles,
  variant = 'full',
}: ExperienceTimelineProps): JSX.Element {
  return (
    <ol className="relative flex flex-col gap-10 border-l border-line pl-6 sm:pl-8">
      {roles.map((role) => (
        <li key={role.id} className="relative">
          <span
            aria-hidden="true"
            className="absolute -left-[1.9rem] top-1.5 size-3 rounded-full border-2 border-background bg-accent sm:-left-[2.4rem]"
          />

          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h3 className="text-lg">{role.role}</h3>
            <p className="text-sm font-medium text-accent">{role.company}</p>
          </div>

          <p className="mt-1 text-sm text-subtle">
            <time dateTime={role.period.start}>{role.period.label}</time>
            <span aria-hidden="true"> · </span>
            {role.location}
          </p>

          <p className="mt-3 max-w-2xl text-sm text-muted">{role.summary}</p>

          {variant === 'full' && role.highlights.length > 0 ? (
            <ul className="mt-4 flex max-w-2xl flex-col gap-2 text-sm text-muted">
              {role.highlights.map((highlight) => (
                <li key={highlight} className="flex gap-2">
                  <span
                    aria-hidden="true"
                    className="mt-2 size-1.5 shrink-0 rounded-full bg-line-strong"
                  />
                  {highlight}
                </li>
              ))}
            </ul>
          ) : null}

          {variant === 'full' && role.technologies.length > 0 ? (
            <ChipList
              items={role.technologies}
              label={`${role.company} technologies`}
              className="mt-5"
            />
          ) : null}

          {variant === 'full' && role.engagements.length > 0 ? (
            <ol className="mt-8 flex flex-col gap-6">
              {role.engagements.map((engagement) => (
                <EngagementItem key={engagement.id} engagement={engagement} />
              ))}
            </ol>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

function EngagementItem({ engagement }: { engagement: Engagement }): JSX.Element {
  return (
    <li className="rounded-md border border-line bg-surface p-5">
      <h4 className="text-base">{engagement.title}</h4>
      <p className="mt-1 text-sm text-subtle">
        <time dateTime={engagement.period.start}>{engagement.period.label}</time>
        {engagement.market ? (
          <>
            <span aria-hidden="true"> · </span>
            {engagement.market}
          </>
        ) : null}
      </p>

      <ul className="mt-4 flex flex-col gap-2 text-sm text-muted">
        {engagement.highlights.map((highlight) => (
          <li key={highlight} className="flex gap-2">
            <span aria-hidden="true" className="mt-2 size-1.5 shrink-0 rounded-full bg-accent/60" />
            {highlight}
          </li>
        ))}
      </ul>

      <ChipList
        items={engagement.technologies}
        label={`${engagement.title} technologies`}
        className="mt-5"
      />

      {engagement.caseStudySlug ? (
        <Link
          to={`/case-studies/${engagement.caseStudySlug}`}
          className="mt-5 inline-flex min-h-11 items-center gap-2 text-sm font-medium text-accent no-underline hover:underline"
        >
          Read the case study
          <span className="sr-only">for {engagement.title}</span>
          <ArrowRightIcon width={16} height={16} />
        </Link>
      ) : null}
    </li>
  );
}
